import { ActionTable } from '@/components';
import { useATSelection } from '@/components/table/hooks';
import { useAppSelector } from '@/state';
import { SubmittalsApi } from '@/state/api';
import { RfiSubmittalsType } from '@/types';
import { submittalStatus, submittalType } from '@/types/enums';
import { toDateInputValue } from '@/utils/dateFields';
import { Badge, Box, Button, Center, Flex, Icon, Text } from '@chakra-ui/react';
import { Card, OverlayDialog, PopoverLazy } from '@ui';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { LuCornerDownRight, LuFolderTree } from 'react-icons/lu';
import ExportSubmittalLogModal from './ExportSubmittalLogModal';
import ManageCard from './ManageCard';
import SubmittalDetails from './SubmittalDetails';
import {
  flattenSubmittalHierarchy,
  includeSubmittalAncestors,
  sortSubmittalHierarchy,
} from './utils/submittalHierarchy';

const STATUS_BADGE_PALETTE: Record<string, string> = {
  draft: 'gray',
  submitted: 'blue',
  resubmittal_required: 'orange',
  closed: 'green',
  void: 'purple',
};

const STATUS_ROW_BG: Record<string, string | undefined> = {
  closed: 'rgba(34, 197, 94, 0.22)',
  submitted: undefined,
  resubmittal_required: 'rgba(239, 68, 68, 0.24)',
  void: 'rgba(168, 85, 247, 0.20)',
};

const Submittals = () => {
  const [showSlideForSubmittalItem, setShowSlideForSubmittalItem] = useState<{
    submittal_id: number;
  } | null>(null);

  const selectedProject = useAppSelector(
    (state) => state.project.selectedProject,
  );

  const [postSubmittalsExportLog] =
    SubmittalsApi.Api.usePostSubmittalsExportLogMutation();

  const { data: allSubmittals, isLoading: isLoadingAllSubmittals } =
    SubmittalsApi.Api.useGetAllSubmittalsQuery(
      {
        query: {
          project: selectedProject?.id as number,
        },
      },
      {
        skip: !selectedProject?.id,
      },
    );

  const { selection } = useATSelection();
  const { filterValue, filterCols } = ActionTable.useFilter();
  const { filtersAdvanced } = ActionTable.useFilterAdvanced();

  const displaySubmittals = useMemo(() => {
    const submittals = allSubmittals?.data ?? [];
    const matches = ActionTable.processFilters(
      submittals,
      filterValue,
      filterCols,
      filtersAdvanced,
    );
    return flattenSubmittalHierarchy(
      includeSubmittalAncestors(submittals, matches),
    );
  }, [allSubmittals?.data, filterCols, filterValue, filtersAdvanced]);

  const childCountByParent = useMemo(() => {
    const counts = new Map<number, number>();
    (allSubmittals?.data ?? []).forEach((submittal) => {
      if (submittal.parent == null) return;
      counts.set(submittal.parent, (counts.get(submittal.parent) ?? 0) + 1);
    });
    return counts;
  }, [allSubmittals?.data]);

  const visibleRootCount = displaySubmittals.filter(
    (submittal) => submittal._depth === 0,
  ).length;
  const visibleChildCount = displaySubmittals.length - visibleRootCount;

  if (showSlideForSubmittalItem) {
    return (
      <Flex flexDirection="column" gapY={4}>
        <SubmittalDetails
          submittal_id={showSlideForSubmittalItem?.submittal_id}
          onClose={() => setShowSlideForSubmittalItem(null)}
        />
      </Flex>
    );
  }

  const renderCell = (
    props: ActionTable.RenderCellType<
      SubmittalsTableColumnType,
      { actions: null }
    >,
  ) => {
    const { colKey, cellValue, rowValue } = props;

    const fmtDate = (v: any) => toDateInputValue(v) || '—';

    switch (colKey) {
      case 'number': {
        const depth = rowValue?._depth ?? 0;
        const childCount = childCountByParent.get(rowValue.id) ?? 0;
        const isGroup = depth === 0 && childCount > 0;
        const hierarchyLabel =
          depth === 0
            ? childCount > 0
              ? `${childCount} direct ${childCount === 1 ? 'child' : 'children'}`
              : 'Standalone submittal'
            : depth === 1
              ? 'Child submittal'
              : `Level ${depth + 1} submittal`;

        return (
          <Flex
            alignItems="center"
            gap={2.5}
            minW={0}
            pl={`${depth * 22}px`}
            role="treeitem"
            aria-level={depth + 1}
            aria-label={`${depth > 0 ? `Child level ${depth}. ` : ''}${cellValue}`}
          >
            <Flex
              aria-hidden="true"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
              boxSize="28px"
              borderRadius="md"
              bg={depth === 0 ? 'blue.subtle' : 'bg.subtle'}
              color={depth === 0 ? 'blue.fg' : 'fg.muted'}
            >
              <Icon
                as={depth === 0 ? LuFolderTree : LuCornerDownRight}
                boxSize={depth === 0 ? 4 : 3.5}
              />
            </Flex>

            <Flex direction="column" minW={0} gap={0.5}>
              <Text
                overflow="hidden"
                textOverflow="ellipsis"
                whiteSpace="nowrap"
                fontWeight={
                  isGroup ? 'bold' : depth === 0 ? 'semibold' : 'medium'
                }
                color={depth === 0 ? 'fg' : 'fg.muted'}
              >
                {cellValue ?? '—'}
              </Text>
              <Text
                fontSize="xs"
                color={isGroup ? 'blue.fg' : 'fg.subtle'}
                whiteSpace="nowrap"
              >
                {hierarchyLabel}
              </Text>
            </Flex>
          </Flex>
        );
      }

      case 'title':
        return (
          <Text
            fontWeight={
              rowValue._depth === 0 &&
              (childCountByParent.get(rowValue.id) ?? 0) > 0
                ? 'semibold'
                : 'normal'
            }
            lineClamp={2}
          >
            {cellValue ?? '—'}
          </Text>
        );

      case 'due_at':
        return <Text>{fmtDate(cellValue)}</Text>;

      case 'submitted_date':
        return <Text>{fmtDate(cellValue)}</Text>;

      case 'returned_date':
        return <Text>{fmtDate(cellValue)}</Text>;

      case 'status':
        return (
          <Badge
            colorPalette={STATUS_BADGE_PALETTE[String(cellValue)] ?? 'gray'}
            variant="subtle"
            borderRadius="full"
            px={2.5}
            py={1}
            whiteSpace="nowrap"
          >
            {submittalStatus?.find((s) => s.value === cellValue)?.label ??
              cellValue}
          </Badge>
        );

      case 'submittal_type':
        return (
          <Text>
            {submittalType?.find((s) => s.value === cellValue)?.label ??
              cellValue}
          </Text>
        );

      case 'actions': {
        const item = rowValue;
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
              e.stopPropagation();
              if (item) setShowSlideForSubmittalItem({ submittal_id: item.id });
            }}
          >
            View
          </Button>
        );
      }

      default:
        return cellValue?.value ?? cellValue ?? '';
    }
  };

  return (
    <Flex flexDirection="column" gapY={4}>
      <Card>
        <Flex justifyContent="space-between" alignItems="center">
          <Flex direction="column" gap={0.5}>
            <Text fontWeight="semibold">Submittals</Text>
            <Text fontSize="sm" color="fg.muted">
              {visibleRootCount} parent or standalone rows · {visibleChildCount}{' '}
              linked children
            </Text>
          </Flex>

          <Flex gap={2}>
            {!selection?.getArray().length ? (
              <PopoverLazy
                button={<Button variant="outline">Export Log</Button>}
                popOverTitle=""
              >
                <Flex flexDirection="column" gapY={4}>
                  <Text fontWeight="bold" fontSize="md">
                    Cannot Export
                  </Text>
                  <Text>
                    Please select submittals from the table below to export.
                  </Text>
                </Flex>
              </PopoverLazy>
            ) : (
              <Button
                variant="outline"
                onClick={() => {
                  const selected = (allSubmittals?.data ?? []).filter((s) =>
                    selection?.map((row) => row.id).includes(s.id),
                  );

                  OverlayDialog.open('exportSubmittalLog', {
                    overlayId: 'exportSubmittalLog',
                    title: `Export Submittal Log (${selected.length})`,
                    content: (
                      <ExportSubmittalLogModal
                        selectedSubmittals={selected}
                        onExport={async (rows) => {
                          const blob = await postSubmittalsExportLog({
                            body: { rows },
                          }).unwrap();

                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `submittal_log_${dayjs().format('YYYY-MM-DD')}.pdf`;
                          document.body.appendChild(a);
                          a.click();
                          a.remove();
                          window.URL.revokeObjectURL(url);

                          OverlayDialog.close('exportSubmittalLog');
                        }}
                      />
                    ),
                  });
                }}
              >
                Export Log
              </Button>
            )}

            <Button
              onClick={() => {
                OverlayDialog.open('addSubmittal', {
                  overlayId: 'addSubmittal',
                  title: 'Create Submittal',
                  content: <ManageCard isNew overlayId="addSubmittal" />,
                });
              }}
            >
              Add Submittal
            </Button>
          </Flex>
        </Flex>

        <Center>
          <Flex flexDirection="column" gapY={2} minW={0}>
            <ActionTable.FilterBar
              disabled={!selectedProject?.id}
              filterConfigComponents={filterConfigComponents}
            />
            <Box
              key={`submittal-table-${selectedProject?.id}`}
              display="flex"
              flexDirection="row"
              gap={5}
            >
              <Box h="100%" my={5}>
                <ActionTable<SubmittalsTableColumnType, { actions: null }>
                  width="91.5vw"
                  columns={columns}
                  columnToUseAsRowKey="id"
                  items={displaySubmittals}
                  isPaginated={false}
                  isPaginatedAsync={false}
                  totalItemsCount={displaySubmittals.length}
                  isSortable
                  sortItems={sortSubmittalHierarchy}
                  isFilterable={false}
                  isShowActionBar={false}
                  height="58vh"
                  rowHeight={84}
                  renderCell={renderCell}
                  renderRow={({ rowValue }) => {
                    const depth = rowValue._depth ?? 0;
                    const isGroup =
                      depth === 0 &&
                      (childCountByParent.get(rowValue.id) ?? 0) > 0;
                    const statusBg = STATUS_ROW_BG[rowValue.status];
                    return {
                      rowProps: {
                        bg: statusBg ?? 'transparent',
                        borderTopWidth: isGroup ? '2px' : '0',
                        borderTopColor: isGroup ? 'blue.400' : 'transparent',
                        borderLeftWidth: depth > 0 ? '3px' : '0',
                        borderLeftColor: depth > 1 ? 'purple.300' : 'blue.200',
                        _hover: {
                          bg: statusBg ?? 'primary.muted',
                        },
                      },
                    } as any;
                  }}
                  isLoading={isLoadingAllSubmittals}
                  isMultipleSelection
                  isCheckbox
                  onRowDoubleClick={(row) =>
                    setShowSlideForSubmittalItem({ submittal_id: row.id })
                  }
                />
              </Box>
            </Box>
          </Flex>
        </Center>
      </Card>
    </Flex>
  );
};

export default () => (
  <ActionTable.Provider>
    <Submittals />
  </ActionTable.Provider>
);

type SubmittalsTableColumnType =
  RfiSubmittalsType.Submittals.Get['response']['data'][number] & {
    _depth: number;
    submitted_date?: string | null;
    returned_date?: string | null;
  };

const columns: ActionTable.ColumnsConfigType<
  SubmittalsTableColumnType,
  { actions: null }
> = [
  {
    number: {
      label: 'Submittal #',
      width: '230px',
    },
  },
  {
    title: {
      label: 'Title',
      width: '300px',
    },
  },
  {
    submittal_type: {
      label: 'Type',
      width: '120px',
    },
  },
  {
    status: {
      label: 'Status',
      width: '160px',
    },
  },
  {
    spec_section: {
      label: 'Spec Section',
      width: '160px',
    },
  },
  {
    vendor: {
      label: 'Vendor',
      width: '160px',
    },
  },
  {
    submitted_date: {
      label: 'Submitted Date',
      width: '140px',
    },
  },
  {
    due_at: {
      label: 'Due Date',
      width: '140px',
    },
  },
  {
    returned_date: {
      label: 'Returned Date',
      width: '140px',
    },
  },
  {
    actions: {
      label: 'Actions',
      width: '120px',
    },
  },
];

const filterConfigComponents: ActionTable.FilterConfigComponentType<SubmittalsTableColumnType>[] =
  [
    {
      filterComponent: 'select',
      filterComponentLabel: 'Status',
      filterConfigItems: submittalStatus.map((status) => ({
        colKeys: ['status'],
        value: status?.label,
        operator: 'contains',
      })),
    },
    {
      filterComponent: 'input',
      filterComponentLabel: 'Subject',
      filterConfigItems: [
        {
          colKeys: ['title'],
          value: '',
          operator: 'contains',
        },
      ],
    },
    {
      filterComponent: 'select',
      filterComponentLabel: 'Type',
      filterConfigItems: submittalType.map((status) => ({
        colKeys: ['submittal_type'],
        value: status?.label,
        operator: 'contains',
      })),
    },
  ];
